import { useState } from 'react';
import type { Dayjs } from 'dayjs';
import { cn } from '@rocco/utils/cn';
import { Spinner } from '@rocco/components/spinner';
import { getResizedImageURL } from '~/modules/images';
import type { MessageAttachment } from '../../types';
import { ChatLightbox, type LightboxItem } from '../lightbox/ChatLightbox';
import { ChatMessageAttachmentActions } from './ChatMessageAttachmentActions';

/**
 * A grid of images for a chat message with image attachments.
 * {@link ChatMessageBubble}
 */
export function ChatMessageImageGrid({
    className,
    images,
    date,
    isOutgoing,
    isOptimistic,
}: {
    className?: string;
    images: MessageAttachment[];
    date: Dayjs;
    isOutgoing: boolean;
    isOptimistic: boolean;
}) {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const lightboxImages = images.map<LightboxItem>((img) => ({
        id: img.ID,
        url: img.Link,
        filename: img.FileName ?? '',
        date,
    }));

    return (
        <>
            <ul
                className={cn(
                    'grid gap-[4px]',
                    // Take up width if 1 image, grid if 2+.
                    images.length <= 1
                        ? 'grid-cols-1'
                        : 'auto-rows-[120px] grid-cols-2',
                    images.length <= 1 && isOutgoing && 'justify-items-end',
                    className,
                )}
            >
                {images.map((att, index) => (
                    <ChatMessageImage
                        key={att.ID}
                        className={cn(
                            images.length <= 1 &&
                                'max-h-[200px] min-h-[50px] w-fit min-w-[80px]',
                        )}
                        src={att.Link}
                        filename={att.FileName ?? ''}
                        isOptimistic={isOptimistic}
                        onClick={() => {
                            setLightboxIndex(index);
                            setLightboxOpen(true);
                        }}
                    />
                ))}
            </ul>
            <ChatLightbox
                open={lightboxOpen}
                index={lightboxIndex}
                items={lightboxImages}
                onOpenChange={setLightboxOpen}
                onIndexChange={setLightboxIndex}
            />
        </>
    );
}

/**
 * A single image tile inside a {@link ChatMessageImageGrid}.
 */
function ChatMessageImage({
    className,
    src,
    filename,
    isOptimistic,
    onClick,
}: {
    className?: string;
    src: string;
    filename: string;
    isOptimistic: boolean;
    onClick?: () => void;
}) {
    const resizedSrc = getResizedImageURL(src, 400, 400);

    return (
        <li
            className={cn(
                'group relative overflow-hidden rounded-[4px] border border-gray-300 focus-visible:border-gray-400',
                isOptimistic ? 'cursor-default' : 'cursor-zoom-in',
                className,
            )}
            role={isOptimistic ? undefined : 'button'}
            tabIndex={isOptimistic ? -1 : 0}
            aria-label={`View ${filename}`}
            aria-disabled={isOptimistic}
            onClick={() => {
                if (isOptimistic) return;
                onClick?.();
            }}
            onKeyDown={(e) => {
                if (isOptimistic) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
        >
            <img
                className="size-full select-none object-cover"
                src={resizedSrc}
                alt={filename}
                loading="lazy"
                draggable={false}
            />
            {!isOptimistic && (
                <ChatMessageAttachmentActions
                    className="absolute right-[6px] top-[6px] opacity-0 transition-opacity duration-100 group-hover:opacity-100"
                    url={src}
                    filename={filename}
                />
            )}
            {isOptimistic && (
                <div className="absolute inset-0 grid place-items-center bg-black/20">
                    <Spinner className="size-[32px] max-h-[80%] max-w-[80%] text-white" />
                </div>
            )}
        </li>
    );
}
